import { useCallback } from "react";
import {
  useFlag,
  useFlagsStatus,
  useUnleashClient,
  IMutableContext,
} from "@unleash/proxy-client-react";
import { addFeatureFlagEvaluation } from "../o11y";

/**
 * Outside of a provider, the Unleash react SDK hands back a stub client that is missing
 * most of its methods, so anything we call on it fails with an unhelpful TypeError.
 * Checking for one of the missing methods lets us fail during render with a message that
 * says what actually went wrong.
 */
function useRequiredUnleashClient() {
  const client = useUnleashClient();

  if (typeof client?.getContext !== "function") {
    throw new Error(
      "This hook must be used within a FeatureFlagProvider from @jsq/flags-react",
    );
  }

  return client;
}

/**
 * The react provider and the raw js client use the `ready` term to mean different things.
 * The react status means that the provider is set up to respond to toggle requests
 * The client status means it was actually able to contact the network and get the flags
 * We need both to be true to mean that the client is ready to use.
 */
function useIsClientReady() {
  const { flagsReady } = useFlagsStatus();
  const client = useUnleashClient();

  return flagsReady && client.isReady();
}

/**
 * Check if a flag is enabled or not in Unleash. Please note that we do not support variant flags yet.
 * It is highly recommended to create a wrapping hook for each specific flag so that all evaluations
 * use the same `defaultValue`. See the `@jsq/flags-react` README for more information.
 * @param flagKey The flag key
 * @param defaultValue The default value to return if the client is not ready
 * @returns Whether the flag is enabled or not
 */
export function useFeatureFlag(flagKey: string, defaultValue: boolean): boolean {
  const isReady = useIsClientReady();
  const isEnabled = useFlag(flagKey);

  if (!isReady) return defaultValue;

  addFeatureFlagEvaluation(flagKey, isEnabled);

  return isEnabled;
}

// these values can be set as properties in our useUpdateContext hook
type ContextValue = string | number | null | undefined;
// These unleash built-in fields are not allowed to be set as properties in our
// useUpdateContext hook
type BuiltInField = Exclude<keyof IMutableContext, "properties" | "userId">;

// Allows any custom context field name, but exclude the unleash built-in fields
type ContextFields = Record<string, ContextValue> & Partial<Record<BuiltInField, never>>;

/**
 * A hook to update the context that is used to base flag evaluations on.
 * @returns A function that takes a fields object and updates the context. Field keys should always be camelCase. Pass null or undefined to remove a field.
 */
export function useUpdateContext() {
  const client = useRequiredUnleashClient();

  const updateCallback = useCallback(
    async (fields: ContextFields) => {
      // Unleash's updateContext replaces the mutable context rather than merging, so we
      // have to merge all the fields for them.
      // They have a setContextField function, but it operates on a single field at a time,
      // and is async, the app will reload between each call with partial context.
      const { properties: currentProperties, userId: currentUserId } = client.getContext();

      const nextProperties: Record<string, string> = { ...currentProperties };
      let nextUserId = currentUserId;

      for (const [field, value] of Object.entries(fields)) {
        // remove the field if it is null or undefined, stringify in case it's a number
        const nextValue = value === null || value === undefined ? undefined : String(value);

        if (field === "userId") {
          // userId is a special case, needs to not be in the properties object
          nextUserId = nextValue;
        } else if (nextValue === undefined) {
          delete nextProperties[field];
        } else {
          nextProperties[field] = nextValue;
        }
      }

      await client.updateContext({
        ...(nextUserId !== undefined ? { userId: nextUserId } : {}),
        properties: nextProperties,
      });
    },
    [client],
  );
  return updateCallback;
}

/**
 * Identify the arena that flag evaluations should be based on. If you need to unset the arena, call the hook with null or undefined.
 * @returns A function that takes an arenaId and updates the context.
 */
export function useIdentifyArena() {
  const updateContext = useUpdateContext();

  const identifyArena = useCallback(
    async (arenaId?: string | number | null): Promise<void> => {
      await updateContext({ arenaId });
    },
    [updateContext],
  );
  return identifyArena;
}
